/**
 * Hook scoped to VAIR_PLAYER_SEARCH_API (v1/vair/player-search).
 * Single source of truth for Vair user data from the player search endpoint.
 */
import { useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import {
  VAIR_PLAYER_SEARCH_API,
  VairPlayerSearchResponse,
  VairPlayersPayload,
  VairUser,
} from 'constants/payloads/vair/playerSearch';
import { AuthStatus } from 'constants/auth';
import { useApiGateway } from 'hooks/useApi';
import { useGetCurrentUser } from './useGetCurrentUser';
import { useViewer } from './useViewer';

export type { VairUser };

export interface UseVairPlayerSearchReturn {
  vairUser: VairUser | null;
  vairUserLoading: boolean;
  /**
   * True once we have a definitive answer for the VAIR user — i.e. the player
   * search request has settled (or there's no user to look up). Unlike
   * `vairUserLoading` this also covers the idle window before the request
   * fires, so callers can render a skeleton without a flash of empty state.
   */
  vairUserResolved: boolean;
  refreshVairUser: () => Promise<void>;
}

export const useVairPlayerSearch = (): UseVairPlayerSearchReturn => {
  const { user } = useGetCurrentUser();
  const viewer = useViewer();

  const {
    post: fetchVairPlayerSearch,
    data: vairPlayerSearchData,
    isLoading: vairUserLoading,
    isCalled: vairUserCalled,
  } = useApiGateway<VairPlayersPayload, VairPlayerSearchResponse>(VAIR_PLAYER_SEARCH_API);

  // Resolution keyed off auth status so it terminates for logged-out users too:
  //  - Loading   → auth undecided, keep waiting
  //  - Anonymous → no account, nothing to fetch, resolved
  //  - User      → resolved once the players request has settled
  const vairUserResolved =
    viewer.status === AuthStatus.Loading
      ? false
      : viewer.status === AuthStatus.Anonymous
        ? true
        : vairUserCalled && !vairUserLoading;

  const refreshVairUser = useCallback(async () => {
    if (!user?.id) return;

    try {
      await fetchVairPlayerSearch({ payload: { userIds: [user.id] } });
    } catch (error) {
      console.error('Error fetching Vair user data:', error);
      Sentry.captureException(error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      refreshVairUser();
    }
  }, [user?.id]);

  return {
    vairUser: (user?.id ? vairPlayerSearchData?.[user.id] : null) ?? null,
    vairUserLoading,
    vairUserResolved,
    refreshVairUser,
  };
};
